import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, HandHeart, X } from 'lucide-react';
import communicationImg from '../../assets/images/parent-domains/communication.jpg';
import socialPlayImg from '../../assets/images/parent-domains/social-play.jpg';
import dailySkillsImg from '../../assets/images/parent-domains/daily-skills.jpg';
import emotionalRegulationImg from '../../assets/images/parent-domains/emotional-regulation.jpg';
import cognitiveImg from '../../assets/images/parent-domains/cognitive.jpg';
import motorSkillsImg from '../../assets/images/parent-domains/motor-skills.jpg';
import schoolReadinessImg from '../../assets/images/parent-domains/school-readiness.jpg';
import sensoryImg from '../../assets/images/parent-domains/sensory.jpg';
import { parentBlogs } from '../../data/parentBlogs';
import {
  getTopGoalsForCategories,
  loadTherapistGoalLibrary,
  type GoalCategory,
  type TherapistGoalLibraryItem,
} from '../../utils/therapistGoalLibrary';

type HeroKey = 'social' | 'communication' | 'cognitive' | 'motor' | 'daily' | 'emotional' | 'school' | 'sensory';

interface DomainCard {
  domainId: string;
  title: string;
  subtitle: string;
  image: string;
  categories: GoalCategory[];
}

interface ResourceCard {
  category: string;
  title: string;
  description: string;
  source: string;
  url: string;
}

const heroImageByKey: Record<HeroKey, string> = {
  social: socialPlayImg,
  communication: communicationImg,
  cognitive: cognitiveImg,
  motor: motorSkillsImg,
  daily: dailySkillsImg,
  emotional: emotionalRegulationImg,
  school: schoolReadinessImg,
  sensory: sensoryImg,
};

/** When the goal library has no rows for a domain, show these strength-based sample ideas (not clinical prescriptions). */
const FALLBACK_GOALS_BY_DOMAIN: Record<string, Array<{ title: string; description: string }>> = {
  communication: [
    {
      title: 'Express needs and choices',
      description:
        'Practice requesting preferred items or actions using speech, signs, pictures, or AAC—honoring whatever mode works so communication stays low-pressure.',
    },
    {
      title: 'Back-and-forth exchanges',
      description:
        'Build tiny “serve and return” routines: you model a sound or gesture, pause, and wait—so turn-taking feels predictable before words show up.',
    },
    {
      title: 'Understanding daily directions',
      description:
        'Pair short phrases with gestures and visuals so receptive language grows in real routines like meals, transitions, and play.',
    },
    {
      title: 'Joint attention during play',
      description:
        'Follow your child’s interest and narrate what they notice—shared focus is often the runway for richer expression.',
    },
    {
      title: 'Functional phrases over drilling',
      description:
        'Prioritize phrases that reduce frustration first—help, stop, more, wait—before longer sentences.',
    },
    {
      title: 'AAC confidence',
      description:
        'If a device or picture board is in use, practice in calm moments so the tool feels like autonomy, not a test.',
    },
    {
      title: 'Listening with processing time',
      description:
        'Insert longer pauses after questions; many neurodivergent kids process language on a slightly different timeline.',
    },
    {
      title: 'Story and book routines',
      description:
        'Repeat favorite books with predictable phrases your child can finish—rhythm builds participation.',
    },
    {
      title: 'Repairing breakdowns gently',
      description:
        'When misunderstanding happens, show a simpler model instead of repeating the same long instruction louder.',
    },
    {
      title: 'Celebrating attempts',
      description:
        'Reinforce any intentional attempt at connection—approximations matter as much as “perfect” words.',
    },
  ],
  social_play: [
    {
      title: 'Parallel play with a shared focus',
      description:
        'Play beside your child with similar toys so social interest can grow without forced eye contact.',
    },
    {
      title: 'Turn-taking in play',
      description:
        'Use simple games with clear turns and short rounds so “my turn / your turn” becomes muscle memory.',
    },
    {
      title: 'Entering peer play slowly',
      description:
        'Start with one predictable friend and a short, structured activity before open-ended free play.',
    },
    {
      title: 'Reading social cues in context',
      description:
        'Name what peers are doing in plain language during real moments—school, park, siblings—rather than abstract rules.',
    },
    {
      title: 'Flexible play scripts',
      description:
        'If rituals appear, widen choices one step at a time so novelty feels safe.',
    },
    {
      title: 'Cooperative tasks',
      description:
        'Build confidence with simple teamwork—rolling a ball, building a tower together—before larger group demands.',
    },
    {
      title: 'Managing overstimulation',
      description:
        'Plan exits and breaks during social settings so regulation comes before performance.',
    },
    {
      title: 'Interest-led connection',
      description:
        'Join special interests sincerely; motivation is often the fastest bridge to interaction.',
    },
    {
      title: 'Sibling and family routines',
      description:
        'Practice greetings and short joint activities at home where expectations are gentler.',
    },
    {
      title: 'Small-group readiness',
      description:
        'Simulate circle-time length gradually—duration often matters more than content at first.',
    },
  ],
  daily_living: [
    {
      title: 'Morning and bedtime sequences',
      description:
        'Use consistent order with visuals so independence grows through repetition, not nagging.',
    },
    {
      title: 'Dressing steps broken down',
      description:
        'Teach one layer at a time with backwards chaining if helpful—finish together, fade supports slowly.',
    },
    {
      title: 'Mealtime participation',
      description:
        'Focus on sitting, trying, and communicating hunger/fullness before expanding food variety.',
    },
    {
      title: 'Hygiene with sensory respect',
      description:
        'Adjust water temperature, brush pressure, and timers—many differences are sensory, not “refusal.”',
    },
    {
      title: 'Toileting readiness cues',
      description:
        'Watch body signals and keep routines calm; shame slows progress for many kids.',
    },
    {
      title: 'Transitions between home tasks',
      description:
        'Use two-step visuals and countdowns so shifting activities feels planned, not abrupt.',
    },
    {
      title: 'Carrying belongings',
      description:
        'Practice backpack, coat, and shoes as a repeatable routine tied to leaving the house.',
    },
    {
      title: 'Cleaning up as a closed loop',
      description:
        'End activities with a clear “all done” sequence so spaces reset predictably.',
    },
    {
      title: 'Safety in the community',
      description:
        'Teach street crossing, staying close, and who to ask for help using concrete practice.',
    },
    {
      title: 'Self-advocacy in daily needs',
      description:
        'Encourage asking for a break, bathroom, or help—functional communication reduces meltdowns.',
    },
  ],
  emotions_behavior: [
    {
      title: 'Recognizing early stress signals',
      description:
        'Notice pacing, covering ears, or repetitive behavior as communication—not “bad behavior.”',
    },
    {
      title: 'Co-regulation before coaching',
      description:
        'Lower your pace and voice first; regulation often transfers faster than reasoning mid-meltdown.',
    },
    {
      title: 'Predictable transitions',
      description:
        'Announce changes with visuals and timers so surprises don’t drain emotional bandwidth.',
    },
    {
      title: 'Replacement skills for tough moments',
      description:
        'Teach a short script or movement that matches the need—squeeze, walk, ask for break.',
    },
    {
      title: 'Recovery over perfection',
      description:
        'Measure progress by how quickly your child returns to calm, not whether big feelings disappear.',
    },
    {
      title: 'Demand stacking awareness',
      description:
        'Reduce simultaneous requests when possible—many kids need fewer demands per minute.',
    },
    {
      title: 'Sensory-informed behavior plans',
      description:
        'Ask what the environment sounded/felt like before labeling behavior as intentional.',
    },
    {
      title: 'Flexible routines with anchors',
      description:
        'Keep “same enough” anchors while stretching one thing at a time.',
    },
    {
      title: 'Teaching “not yet” and waiting',
      description:
        'Use timers and visuals so delay feels bounded instead of vague.',
    },
    {
      title: 'Repair after hard moments',
      description:
        'Brief reconnect routines rebuild trust—especially after shame-heavy reactions.',
    },
  ],
  learning_thinking: [
    {
      title: 'Attention in short bursts',
      description:
        'Start with achievable durations and celebrate focus, then stretch time gradually.',
    },
    {
      title: 'Visual sequencing',
      description:
        'Break tasks into 3 steps with pictures so working memory isn’t overloaded.',
    },
    {
      title: 'Concrete before abstract',
      description:
        'Use manipulatives and real objects before jumping to worksheets or verbal-only tasks.',
    },
    {
      title: 'Flexible problem-solving',
      description:
        'Offer two acceptable solutions so cognitive flexibility grows without constant correction.',
    },
    {
      title: 'Sorting and categorizing play',
      description:
        'Build early logic skills through real-life sorting—laundry, toys, groceries.',
    },
    {
      title: 'Working memory supports',
      description:
        'Repeat directions in chunks and allow written or picture cues during multi-step tasks.',
    },
    {
      title: 'Early math through routines',
      description:
        'Count stairs, crackers, or turns—numbers anchored in meaningful activity stick better.',
    },
    {
      title: 'Story prediction skills',
      description:
        'Pause books to guess what comes next—prediction strengthens comprehension joyfully.',
    },
    {
      title: 'Errorless learning chunks',
      description:
        'Reduce repeated failure by scaffolding harder items until confidence returns.',
    },
    {
      title: 'Planning the day visually',
      description:
        'A simple “first/then” or calendar strip can reduce anxiety and support executive function.',
    },
  ],
  movement: [
    {
      title: 'Posture and positioning for seated work',
      description:
        'Adjust chair, footrest, or slant board so fine-motor demands aren’t fighting gravity.',
    },
    {
      title: 'Fine motor through daily jobs',
      description:
        'Open packages, stir, zip—real tasks beat endless pencil worksheets for many kids.',
    },
    {
      title: 'Bilateral coordination play',
      description:
        'Crumple paper, use tongs, or cut with adapted scissors—two hands working together in play.',
    },
    {
      title: 'Gross motor confidence',
      description:
        'Climb, jump, and pedal in low-pressure settings to build strength and body awareness.',
    },
    {
      title: 'Handwriting without shame',
      description:
        'Try thicker tools, grips, or keyboarding if fatigue is the barrier—speed can come later.',
    },
    {
      title: 'Crossing midline activities',
      description:
        'Ball games and figure-drawing in the air support coordination used in reading and writing.',
    },
    {
      title: 'Motor planning routines',
      description:
        'Break new motor sequences into slow, repeated practice with the same cues each time.',
    },
    {
      title: 'Outdoor heavy work',
      description:
        'Pushing, pulling, and carrying can improve regulation and motor output for some kids.',
    },
    {
      title: 'Dressing motor skills',
      description:
        'Practice fasteners with clothes off-body first—buttons and zippers need repetition.',
    },
    {
      title: 'Balance and core stability',
      description:
        'Simple animal walks and obstacle paths build the core support fine motor sits on.',
    },
  ],
  school: [
    {
      title: 'Circle-time stamina',
      description:
        'Increase sitting tolerance gradually with sensory tools and scheduled movement breaks.',
    },
    {
      title: 'Following classroom routines',
      description:
        'Match school visuals to home practice so expectations feel consistent across settings.',
    },
    {
      title: 'Transitions between subjects',
      description:
        'Use timers and checklists so shifting tasks doesn’t drain all available attention.',
    },
    {
      title: 'Asking for help at school',
      description:
        'Practice a simple script or card system so self-advocacy is possible when confused.',
    },
    {
      title: 'Organizing materials',
      description:
        'Color-coded folders and picture lists reduce working-memory load during busy days.',
    },
    {
      title: 'Participating in group work',
      description:
        'Start with a defined role for short intervals before open-ended collaboration.',
    },
    {
      title: 'Locker and hallway routines',
      description:
        'Rehearse sequences after school until they’re automatic—novel environments cost energy.',
    },
    {
      title: 'Listening for key words',
      description:
        'Highlight “must-do” words on directions so receptive overload is smaller.',
    },
    {
      title: 'Friendship skills at school',
      description:
        'Practice greeting, joining, and exiting play with scripts tied to recess realities.',
    },
    {
      title: 'Self-regulation in class',
      description:
        'Teach discreet breaks—water, bathroom, squeeze tool—before escalation.',
    },
  ],
  sensory: [
    {
      title: 'Sensory profile awareness',
      description:
        'Track what seeks calm versus what amps your child up—profiles aren’t “good/bad,” just information.',
    },
    {
      title: 'Predictable sensory prep',
      description:
        'Before noisy or crowded settings, build in heavy work or quiet warm-up routines.',
    },
    {
      title: 'Noise reduction options',
      description:
        'Headphones, quieter seating, or previewing sounds can prevent overload.',
    },
    {
      title: 'Texture exploration on their terms',
      description:
        'Offer choices and escape hatches—gradual exposure works best without force.',
    },
    {
      title: 'Movement breaks that match needs',
      description:
        'Some kids need bouncing or spinning; others need stillness—match the individual.',
    },
    {
      title: 'Lighting and visual clutter',
      description:
        'Reduce fluorescent flicker and busy walls if they consistently trigger fatigue.',
    },
    {
      title: 'Mealtime sensory supports',
      description:
        'Separate foods, offer utensils that feel right, and keep pressure low around trying.',
    },
    {
      title: 'Calm-down toolkit',
      description:
        'A basket of agreed tools—blanket, stress ball, visuals—creates autonomy in dysregulation.',
    },
    {
      title: 'Co-regulating during overload',
      description:
        'Stay steady, reduce language, and change the environment first when possible.',
    },
    {
      title: 'Sleep and sensory winding down',
      description:
        'Dim lights, predictable sequence, and heavy blanket options can support evening regulation.',
    },
  ],
};

const domainCards: DomainCard[] = [
  {
    domainId: 'communication',
    title: 'Communication',
    subtitle: 'Speech, language, asking, understanding',
    image: communicationImg,
    categories: ['LANGUAGE'],
  },
  {
    domainId: 'social_play',
    title: 'Social & Play',
    subtitle: 'Turn-taking, shared play, peer interaction',
    image: socialPlayImg,
    categories: ['SOCIAL'],
  },
  {
    domainId: 'daily_living',
    title: 'Daily Living',
    subtitle: 'Dressing, hygiene, eating, independence',
    image: dailySkillsImg,
    categories: ['SELF_HELP'],
  },
  {
    domainId: 'emotions_behavior',
    title: 'Emotions & Behavior',
    subtitle: 'Regulation, transitions, coping skills',
    image: emotionalRegulationImg,
    categories: ['EMOTIONAL', 'BEHAVIOURAL'],
  },
  {
    domainId: 'learning_thinking',
    title: 'Learning & Thinking',
    subtitle: 'Problem solving, attention, early learning',
    image: cognitiveImg,
    categories: ['COGNITION'],
  },
  {
    domainId: 'movement',
    title: 'Movement & Coordination',
    subtitle: 'Fine motor, gross motor, body control',
    image: motorSkillsImg,
    categories: ['MOTOR'],
  },
  {
    domainId: 'school',
    title: 'School Skills',
    subtitle: 'Classroom readiness, routines, participation',
    image: schoolReadinessImg,
    categories: ['COGNITION', 'LANGUAGE'],
  },
  {
    domainId: 'sensory',
    title: 'Sensory Comfort',
    subtitle: 'Sounds, textures, movement, regulation',
    image: sensoryImg,
    categories: ['SENSORY'],
  },
];

const resourceCards: ResourceCard[] = [
  {
    category: 'Autism basics',
    title: 'Autism Speaks Tool Kits',
    description: 'Free guides for families covering autism diagnosis, daily life, safety, and support.',
    source: 'Autism Speaks',
    url: 'https://www.autismspeaks.org/tool-kit',
  },
  {
    category: 'Autism basics',
    title: 'CDC Autism Resources',
    description: 'Developmental milestones, autism signs, screening information, and family guidance.',
    source: 'CDC',
    url: 'https://www.cdc.gov/autism/',
  },
  {
    category: 'IEP & school advocacy',
    title: 'Understood IEP Guide',
    description: 'Parent-friendly explanations of IEPs, accommodations, and school supports.',
    source: 'Understood',
    url: 'https://www.understood.org/en/articles/what-is-an-iep',
  },
  {
    category: 'IEP & school advocacy',
    title: 'Wrightslaw Special Education Law',
    description: 'Special education law, advocacy articles, IEP rights, and parent training resources.',
    source: 'Wrightslaw',
    url: 'https://www.wrightslaw.com/',
  },
  {
    category: 'Parent strategies',
    title: 'Parent Center Hub',
    description: 'Find parent training and information centers that help families understand rights and services.',
    source: 'Parent Center Hub',
    url: 'https://www.parentcenterhub.org/',
  },
  {
    category: 'OT & sensory',
    title: 'The OT Toolbox',
    description: 'Occupational therapy activities for sensory, fine motor, handwriting, and daily routines.',
    source: 'The OT Toolbox',
    url: 'https://www.theottoolbox.com/',
  },
  {
    category: 'Speech & communication',
    title: 'Speech and Language Kids',
    description: 'Parent-friendly speech therapy ideas, language strategies, and home practice resources.',
    source: 'Speech and Language Kids',
    url: 'https://www.speechandlanguagekids.com/',
  },
  {
    category: 'Behavior & regulation',
    title: 'Autism Parenting Magazine',
    description: 'Parent articles on autism strategies, behavior, communication, and daily family life.',
    source: 'Autism Parenting Magazine',
    url: 'https://www.autismparentingmagazine.com/',
  },
];

const whyCards = [
  'Real-life goals',
  'Parent-friendly strategies',
  'Affordable therapy discovery',
  'Free tools before you pay',
];

function SectionBand({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`w-full ${className}`}>
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-20">{children}</div>
    </section>
  );
}

export default function ParentLandingPage() {
  const navigate = useNavigate();
  const [goalLibrary, setGoalLibrary] = useState<TherapistGoalLibraryItem[] | null>(null);
  const [goalsLoadError, setGoalsLoadError] = useState<string | null>(null);
  const [goalsModalDomain, setGoalsModalDomain] = useState<DomainCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const lib = await loadTherapistGoalLibrary();
        if (!cancelled) setGoalLibrary(lib);
      } catch (e) {
        if (!cancelled) {
          setGoalsLoadError(e instanceof Error ? e.message : 'Could not load goals.');
          setGoalLibrary([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!goalsModalDomain) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setGoalsModalDomain(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [goalsModalDomain]);

  const modalTiles = useMemo(() => {
    if (!goalsModalDomain) return null;

    if (goalLibrary === null) {
      return null;
    }

    const fromLib =
      goalLibrary.length > 0
        ? getTopGoalsForCategories(goalLibrary, goalsModalDomain.categories, 10)
        : [];

    if (fromLib.length > 0) {
      return fromLib.map((g) => ({
        key: g.goal_id,
        title: g.goal_title,
        description: g.goal_description ?? '',
        source: 'library' as const,
      }));
    }

    const samples = (FALLBACK_GOALS_BY_DOMAIN[goalsModalDomain.domainId] ?? []).slice(0, 10);
    return samples.map((s, i) => ({
      key: `sample-${goalsModalDomain.domainId}-${i}`,
      title: s.title,
      description: s.description,
      source: 'sample' as const,
    }));
  }, [goalsModalDomain, goalLibrary]);

  const openGoalsModal = useCallback((domain: DomainCard) => {
    setGoalsModalDomain(domain);
  }, []);

  const closeGoalsModal = useCallback(() => {
    setGoalsModalDomain(null);
  }, []);

  return (
    <div className="min-h-screen bg-stone-500 text-slate-900">
      <SectionBand className="bg-stone-400">
        <div className="grid min-h-0 grid-cols-1 items-center gap-8 py-10 sm:gap-10 lg:min-h-[720px] lg:grid-cols-2 lg:py-0">
          <div className="text-left">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">TinyWins for Parents</p>
            <h1 className="mt-4 text-3xl font-bold leading-tight text-slate-900 sm:text-5xl lg:text-7xl">
              Developmental support designed for parents of neurodivergent children
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-7 text-slate-700 sm:text-lg sm:leading-8">
              Track developmental goals, use sensory-aware strategies, explore IEP-friendly tools, and keep
              progress moving at home — alongside the professionals on your child&apos;s team.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/?openAuth=signUp')}
                className="rounded-full bg-indigo-600 px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
              >
                Create Your Free Parent Account
              </button>
              <button
                type="button"
                onClick={() => navigate('/?openAuth=signIn')}
                className="rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => navigate('/organizations')}
                className="rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
              >
                Find a Therapist
              </button>
            </div>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {['Affordable options', 'Remote & local providers', 'Free parent tools'].map((pill) => (
                <span key={pill} className="rounded-full bg-white/80 px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm">
                  {pill}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-[2rem] bg-white p-3 shadow-xl">
              <img
                src={schoolReadinessImg}
                alt="Parent and child working through development activities"
                className="h-[min(50vh,540px)] w-full rounded-[1.6rem] object-cover sm:h-[540px]"
              />
            </div>
            <div className="absolute -left-4 top-8 rounded-2xl bg-white px-4 py-3 shadow-lg">
              <p className="text-sm font-semibold text-slate-800">Speech - OT - ABA</p>
            </div>
            <div className="absolute -bottom-4 right-6 rounded-2xl bg-indigo-600 px-4 py-3 shadow-lg">
              <p className="text-sm font-semibold text-white">From therapy goals to daily wins</p>
            </div>
          </div>
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-400">
        <h2 className="text-left text-4xl font-bold leading-tight lg:text-5xl">Explore your child&apos;s next small win</h2>
        <p className="mt-4 max-w-4xl text-left text-lg text-slate-600">
          Choose a development area to see goals, strategies, tools, and therapist options connected to real-life progress.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {domainCards.map((domain) => (
            <article
              key={domain.domainId}
              className="group overflow-hidden rounded-3xl bg-white/95 shadow-md ring-1 ring-stone-500/30 transition duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="h-56 overflow-hidden">
                <img
                  src={domain.image}
                  alt={domain.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="h-[35%] p-5 text-left">
                <h3 className="text-xl font-semibold">{domain.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{domain.subtitle}</p>
                <button
                  type="button"
                  onClick={() => openGoalsModal(domain)}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  Explore goals <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-300">
        <div className="text-left">
          <h2 className="text-4xl font-bold leading-tight lg:text-5xl">How families book affordable therapists on TinyWins</h2>
          <p className="mt-4 max-w-5xl text-lg leading-8 text-slate-700">
            TinyWins helps parents find and book affordable child therapists by comparing providers in one place. You can review speech therapy, occupational
            therapy, ABA therapy, behavior support, and remote therapy options before you contact anyone.
          </p>
          <p className="mt-4 max-w-5xl text-lg leading-8 text-slate-700">
            If you are searching for low-cost autism therapy, start with the provider directory to compare pricing model, session format, and availability. Then
            use our free parent tools to practice goals at home while you book the right therapist for your child.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            <p className="rounded-2xl bg-white/95 p-4 text-base leading-7 text-slate-700 shadow-sm ring-1 ring-stone-400/40">
              Compare local and telehealth providers for communication, sensory, social, school, and daily living goals.
            </p>
            <p className="rounded-2xl bg-white/95 p-4 text-base leading-7 text-slate-700 shadow-sm ring-1 ring-stone-400/40">
              Find therapists that fit your budget with self-pay, insurance-friendly, and affordable care options.
            </p>
          </div>
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-300">
        <div id="free-tools">
          <h2 className="text-left text-4xl font-bold leading-tight lg:text-5xl">Free tools parents can use today</h2>
          <p className="mt-4 max-w-3xl text-left text-lg text-slate-600">Start with practical support before you book anything.</p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <article className="overflow-hidden rounded-3xl bg-white/95 shadow-md ring-1 ring-stone-400/40">
            <img src={communicationImg} alt="Comprehension learning support" className="h-60 w-full object-cover" />
            <div className="p-6 text-left">
              <h3 className="text-2xl font-semibold">Comprehension Tool</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Turn stories into guided Q&amp;A with leveled prompts, so reading practice becomes easier to support at home.
              </p>
              <a
                href="https://www.tinywins.ai/comprehension/"
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                Try Comprehension Tool
              </a>
            </div>
          </article>

          <article className="overflow-hidden rounded-3xl bg-white/95 shadow-md ring-1 ring-stone-400/40">
            <img src={socialPlayImg} alt="Social story creation support" className="h-60 w-full object-cover" />
            <div className="p-6 text-left">
              <h3 className="text-2xl font-semibold">Social Stories Tool</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Create personalized social stories for transitions, routines, school readiness, and emotional regulation.
              </p>
              <a
                href="https://www.tinywins.ai/social-stories"
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex rounded-full bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700"
              >
                Create Social Story
              </a>
            </div>
          </article>
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-400">
        <h2 className="text-left text-4xl font-bold leading-tight lg:text-5xl">Trusted free resources for parents</h2>
        <p className="mt-4 max-w-4xl text-left text-lg text-slate-600">
          Curated links for autism support, IEP help, advocacy, behavior, communication, and daily routines.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {resourceCards.map((resource) => (
            <article key={resource.title} className="rounded-2xl bg-white/95 p-5 text-left shadow-sm ring-1 ring-stone-500/30">
              <span className="sr-only">Category: {resource.category}</span>
              <h3 className="mt-4 text-lg font-semibold">{resource.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">{resource.description}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Source: {resource.source}</p>
              <a
                href={resource.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
              >
                Open Resource <ArrowRight className="h-4 w-4" />
              </a>
            </article>
          ))}
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-300">
        <h2 className="text-left text-4xl font-bold leading-tight lg:text-5xl">Parent guides for practical progress</h2>
        <p className="mt-4 max-w-3xl text-left text-lg text-slate-600">
          Short, practical reads focused on what parents can try this week.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {parentBlogs.map((blog, index) => {
            const image = heroImageByKey[blog.heroImage];
            return (
              <article key={blog.slug} className="overflow-hidden rounded-3xl bg-white/95 shadow-md ring-1 ring-stone-400/40">
                <img src={image} alt={blog.title} className="h-52 w-full object-cover" loading="lazy" />
                <div className="p-5 text-left">
                  <span className="sr-only">Category: {blog.category}</span>
                  <h3 className="mt-3 text-lg font-semibold leading-7">{blog.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{blog.intro}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">{blog.readTime}</span>
                    <Link
                      to={`/parents/blog/${blog.slug}`}
                      className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700"
                    >
                      Read guide
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </SectionBand>

      <SectionBand className="bg-stone-400">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl shadow-md">
            <img
              src={dailySkillsImg}
              alt="Parent supporting child through daily routines"
              className="h-[430px] w-full object-cover"
            />
          </div>
          <div className="text-left">
            <h2 className="text-4xl font-bold leading-tight lg:text-5xl">Built for the gap between therapy sessions and real life.</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              Families often leave therapy with advice but not a clear way to practice it during daily routines. TinyWins connects goals, tools, therapists, and parent-friendly strategies so progress can continue at home.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {whyCards.map((item) => (
                <article key={item} className="rounded-2xl bg-white/90 p-4 shadow-sm ring-1 ring-stone-400/40">
                  <div className="inline-flex rounded-full bg-white p-2 text-indigo-600 shadow-sm">
                    <HandHeart className="h-4 w-4" />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-slate-800">{item}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </SectionBand>

      <SectionBand className="bg-indigo-950">
        <div className="text-left">
          <h2 className="text-4xl font-bold leading-tight text-white lg:text-5xl">Start with one small win today.</h2>
          <p className="mt-4 max-w-3xl text-lg text-indigo-200/90">
            Explore goals, try a free tool, or find a therapist who fits your child and your budget.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate('/organizations')}
              className="rounded-full bg-white px-7 py-3 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50"
            >
              Book a Therapist
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('free-tools')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-full bg-indigo-500 px-7 py-3 text-sm font-semibold text-white transition hover:bg-indigo-400"
            >
              Try Free Tools
            </button>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 1200, behavior: 'smooth' })}
              className="rounded-full bg-violet-500 px-7 py-3 text-sm font-semibold text-white transition hover:bg-violet-400"
            >
              Explore Domains
            </button>
          </div>
        </div>
      </SectionBand>

      {goalsModalDomain && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="parent-goals-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeGoalsModal();
          }}
        >
          <div
            className="flex max-h-[94vh] min-h-0 w-[min(96vw,1280px)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-56 w-full shrink-0 sm:h-64 md:h-72">
              <img
                src={goalsModalDomain.image}
                alt={`${goalsModalDomain.title} — developmental area`}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/30" aria-hidden />
              <button
                type="button"
                className="absolute right-3 top-3 rounded-full bg-white/95 p-2 text-slate-700 shadow-md hover:bg-white"
                aria-label="Close"
                onClick={closeGoalsModal}
              >
                <X className="h-5 w-5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-5 pb-4 pt-10 text-left text-white">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/90">Explore goals</p>
                <h2 id="parent-goals-modal-title" className="mt-1 text-2xl font-bold leading-tight sm:text-3xl">
                  {goalsModalDomain.title}
                </h2>
                <p className="mt-1 max-w-xl text-sm text-white/95">{goalsModalDomain.subtitle}</p>
              </div>
            </div>

            <div className="border-b border-slate-200 px-6 py-3 text-left">
              <p className="text-sm text-slate-600">
                {modalTiles && modalTiles.length > 0 && modalTiles[0]?.source === 'library'
                  ? 'Examples from our therapy goal library—illustrative only and not a treatment plan for your child.'
                  : 'Examples of common developmental supports in this area—illustrative ideas only; your therapist personalizes goals.'}
              </p>
              {goalsLoadError ? (
                <p className="mt-2 text-xs text-amber-800">
                  Goal library unavailable ({goalsLoadError}). Showing illustrative examples below.
                </p>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 lg:px-8">
              {goalLibrary === null ? (
                <p className="text-sm text-slate-600">Loading goals…</p>
              ) : modalTiles === null || modalTiles.length === 0 ? (
                <p className="text-sm text-slate-600">No examples could be loaded for this area.</p>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {modalTiles.map((row) => (
                    <article
                      key={row.key}
                      className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-left shadow-sm"
                    >
                      <p className="font-semibold text-slate-900">{row.title}</p>
                      {row.description ? (
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{row.description}</p>
                      ) : null}
                    </article>
                  ))}
                </div>
              )}
            </div>

            <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4 lg:px-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-left text-sm text-slate-700">
                  Ready to match these ideas with support for your child?
                </p>
                <Link
                  to="/organizations"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                  onClick={closeGoalsModal}
                >
                  Find a therapist
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
